import { captureDeferredSentryException } from '@/utils/deferredSentry'

/**
 * 水印配置选项
 */
export interface WatermarkOptions {
  enabled: boolean
  svg?: string // 自定义 SVG 水印
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  size?: number // 水印大小，默认为原图的 10%
  opacity?: number // 透明度 0-1，默认 0.8
}

/**
 * 下载文件的配置选项
 */
export interface DownloadOptions {
  fileName?: string
  useShowSaveFilePicker?: boolean
  watermark?: WatermarkOptions
  onStart?: () => void // 开始下载时的回调
  onSuccess?: () => void
  onError?: (error: Error) => void
}

/**
 * 下载文件
 */
export async function downloadFile(
  url: string,
  options: DownloadOptions = {}
): Promise<void> {
  const {
    fileName,
    useShowSaveFilePicker = false,
    watermark,
    onStart,
    onSuccess,
    onError,
  } = options

  try {
    // 获取原始文件名
    const originalFileName = fileName || getFileNameFromUrl(url)

    // 标记是否已经触发了开始下载的回调，防止重复调用
    let hasCalledOnStart = false

    // 安全调用onStart函数（确保只调用一次）
    const safeOnStart = () => {
      if (!hasCalledOnStart && onStart) {
        hasCalledOnStart = true
        onStart()
      }
    }

    // 如果使用showSaveFilePicker，先获取用户确认
    if (useShowSaveFilePicker && 'showSaveFilePicker' in window) {
      try {
        if (typeof (window as any).showSaveFilePicker === 'function') {
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: originalFileName || getFileNameFromUrl(url),
          })

          // 用户确认位置后，通知开始下载
          safeOnStart()

          const res = await fetch(url, { mode: 'cors', cache: 'no-store' })
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          let blob = await res.blob()

          // 添加水印处理
          if (watermark?.enabled) {
            blob = await addWatermarkToBlob(blob, watermark)
          }

          const writable = await handle.createWritable()
          await writable.write(blob)
          await writable.close()

          onSuccess?.()
          return
        }
      } catch (err) {
        // 如果用户取消选择，直接返回
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
      }
    }

    // 传统下载方式 - 立即通知开始（确保只调用一次）
    safeOnStart()

    const res = await fetch(url, { mode: 'cors', cache: 'no-store' })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    let blob = await res.blob()

    // 添加水印处理
    if (watermark?.enabled) {
      blob = await addWatermarkToBlob(blob, watermark)
    }

    const finalFileName = fileName || getFileNameFromUrl(url)
    await saveBlob(blob, finalFileName)

    onSuccess?.()
  } catch (error) {
    const err = error instanceof Error ? error : new Error('Download failed')
    captureDeferredSentryException(err)
    onError?.(err)
  }
}

/**
 * 保存 Blob 对象为文件
 */
async function saveBlob(blob: Blob, fileName: string): Promise<void> {
  // 传统下载方式
  const blobUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = fileName
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()

  // 清理
  setTimeout(() => {
    document.body.removeChild(link)
    URL.revokeObjectURL(blobUrl)
  }, 100)
}

/**
 * 从 URL 中提取文件名
 */
function getFileNameFromUrl(url: string): string {
  const segments = url.split('/')
  const fileName = segments[segments.length - 1]
  return fileName || 'download'
}

/**
 * 默认的 TipsyAI 水印 SVG
 */
const DEFAULT_WATERMARK_SVG = `
  <svg width="90" height="32" viewBox="0 0 90 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g id="logo 2" clip-path="url(#clip0_258_7765)">
      <g id="Union" filter="url(#filter0_d_258_7765)">
        <path d="M61.502 11.7637C62.605 11.7639 63.3777 12.8536 63.0127 13.8945L59.1699 24.8477C58.9495 25.4831 58.6498 26.0363 58.2705 26.5078C57.8964 26.9845 57.4226 27.3512 56.8486 27.6074C56.2747 27.8688 55.583 28 54.7734 28C54.3582 28 53.9684 27.966 53.6045 27.8994C53.0874 27.8106 52.829 27.2786 52.9805 26.7764L53.1445 26.2305C53.3133 25.6716 53.9049 25.3899 54.4883 25.4092C54.801 25.4195 55.0704 25.3471 55.2959 25.1934C55.5264 25.0395 55.714 24.7781 55.8574 24.4092C55.9814 24.0867 55.9868 23.7306 55.873 23.4043L52.5508 13.8779C52.1906 12.8441 52.958 11.7639 54.0527 11.7637C54.7651 11.7637 55.3907 12.2373 55.584 12.9229L57.6855 20.376C57.6955 20.411 57.7272 20.4355 57.7637 20.4355C57.8 20.4355 57.8318 20.411 57.8418 20.376L59.9619 12.9268C60.1577 12.2387 60.7865 11.7637 61.502 11.7637ZM33.6309 11.6094C34.4918 11.6094 35.286 11.8352 36.0137 12.2861C36.7414 12.732 37.3231 13.4057 37.7588 14.3076C38.1944 15.2045 38.4121 16.3296 38.4121 17.6826C38.4121 18.9995 38.1997 20.1115 37.7744 21.0186C37.3542 21.9206 36.7796 22.6049 36.0518 23.0713C35.3292 23.5324 34.5198 23.7627 33.623 23.7627C32.9876 23.7627 32.4468 23.6584 32.001 23.4482C31.5602 23.2382 31.1989 22.974 30.917 22.6562C30.6455 22.3452 30.436 22.0317 30.2881 21.7158C30.278 21.6943 30.2562 21.6798 30.2324 21.6797C30.1991 21.6797 30.1711 21.7068 30.1709 21.7402V26.3613C30.1709 27.2655 29.4384 27.9989 28.5342 27.999C27.6299 27.999 26.8965 27.2656 26.8965 26.3613V13.3779C26.8965 12.4864 27.6192 11.7628 28.5107 11.7627H29.1338C29.6814 11.7628 30.125 12.2073 30.125 12.7549V13.6582C30.1253 13.707 30.1651 13.746 30.2139 13.7461C30.2488 13.7461 30.28 13.7259 30.2949 13.6943C30.4371 13.3935 30.6369 13.0883 30.8936 12.7783C31.1702 12.4452 31.5291 12.1676 31.9697 11.9473C32.4156 11.7218 32.9698 11.6094 33.6309 11.6094ZM45.3926 11.6094C46.8837 11.6094 48.0573 11.9244 48.9131 12.5547C49.312 12.8468 49.634 13.1881 49.8789 13.5781C50.355 14.3364 49.6603 15.1774 48.7666 15.2324L48.1953 15.2676C47.7522 15.2948 47.3732 14.9654 47.0908 14.623C46.9216 14.413 46.6986 14.246 46.4219 14.123C46.1504 13.9951 45.8253 13.9307 45.4463 13.9307C44.939 13.9307 44.5106 14.0386 44.1621 14.2539C43.8137 14.464 43.6396 14.746 43.6396 15.0996C43.6397 15.3814 43.7521 15.6198 43.9775 15.8145C44.203 16.0093 44.5904 16.1653 45.1387 16.2832L47.2754 16.7139C48.4232 16.9495 49.2791 17.3289 49.8428 17.8516C50.4066 18.3743 50.6885 19.0614 50.6885 19.9121C50.6884 20.6859 50.4609 21.365 50.0049 21.9492C49.5539 22.5333 48.9337 22.9894 48.1445 23.3174C47.3604 23.6402 46.4557 23.8018 45.4307 23.8018C43.8676 23.8018 42.6219 23.476 41.6943 22.8252C41.289 22.5369 40.9571 22.2035 40.6992 21.8242C40.1542 21.0227 40.9052 20.1066 41.873 20.0557L41.959 20.0508C42.7259 20.0105 43.3633 20.6443 44.001 21.0723C44.3751 21.3183 44.8542 21.4414 45.4385 21.4414C46.0123 21.4414 46.4738 21.3316 46.8223 21.1113C47.1759 20.8858 47.3553 20.5958 47.3604 20.2422C47.3552 19.945 47.2294 19.7013 46.9834 19.5117C46.7374 19.317 46.358 19.1689 45.8457 19.0664L43.8008 18.6592C42.6477 18.4286 41.7894 18.0279 41.2256 17.459C40.6672 16.8903 40.3877 16.1655 40.3877 15.2842C40.3877 14.5257 40.5929 13.8716 41.0029 13.3232C41.418 12.7751 41.9999 12.3528 42.748 12.0557C43.5013 11.7584 44.383 11.6094 45.3926 11.6094ZM71.166 8.09473C71.6114 8.09473 71.9979 8.17974 72.3154 8.36035H72.3164C72.6292 8.52838 72.8838 8.74997 73.0742 9.02637H73.0732C73.2583 9.2852 73.411 9.56471 73.5322 9.86426H73.5332C73.6575 10.1487 73.8118 10.5296 73.9961 11.0049L77.9229 20.5195L78.127 21.0312C78.3044 21.5116 78.4004 21.8966 78.4004 22.1768C78.4004 22.5832 78.2233 22.9479 77.8994 23.2656C77.5742 23.5845 77.1749 23.745 76.7139 23.7451C76.4507 23.7451 76.212 23.6974 76.0068 23.5928V23.5918C75.8131 23.4995 75.6478 23.3723 75.5137 23.21L75.5107 23.2061C75.3804 23.0406 75.2457 22.8025 75.1064 22.502L75.1045 22.4971C74.9745 22.1951 74.8616 21.9298 74.7656 21.7012L74.7627 21.6963V21.6953L74.0957 19.9727H68.2588L67.5898 21.7344C67.3097 22.4716 67.0621 22.9963 66.8447 23.2832L66.8428 23.2852C66.5931 23.606 66.1978 23.7451 65.7109 23.7451C65.2747 23.7451 64.8884 23.5859 64.5596 23.2783C64.2321 22.972 64.0548 22.6099 64.0547 22.1973C64.0547 21.9732 64.0931 21.7446 64.167 21.5127C64.2378 21.2903 64.3529 20.9854 64.5107 20.6016L68.3555 11.0273L68.7441 10.042L68.7451 10.041L68.8672 9.75391C68.9912 9.47948 69.1213 9.2402 69.2568 9.03809L69.2588 9.03516L69.416 8.83496C69.5839 8.64539 69.784 8.48774 70.0137 8.36133C70.3373 8.18031 70.7244 8.09474 71.166 8.09473ZM82.0771 8.09473C82.5836 8.09484 83.0086 8.26414 83.3311 8.61133H83.3301C83.6657 8.95833 83.8144 9.46983 83.8145 10.1055V21.7246C83.8144 22.3612 83.6647 22.8745 83.3301 23.2275L83.3311 23.2285C83.0086 23.5757 82.5836 23.745 82.0771 23.7451C81.5881 23.7451 81.1716 23.574 80.8447 23.2305L80.8418 23.2266C80.5159 22.8678 80.3692 22.3556 80.3691 21.7246V10.1055C80.3692 9.47476 80.5152 8.96413 80.8428 8.61133C81.1641 8.26536 81.5821 8.09475 82.0771 8.09473ZM22.6357 11.7637C23.54 11.7638 24.2725 12.4971 24.2725 13.4014V21.9346C24.2722 22.8386 23.5398 23.5712 22.6357 23.5713C21.7315 23.5713 20.9983 22.8387 20.998 21.9346V13.4014C20.998 12.497 21.7313 11.7637 22.6357 11.7637ZM17.5576 7.82617C18.3155 7.82617 18.9306 8.44046 18.9307 9.19824C18.9307 9.95609 18.3155 10.5703 17.5576 10.5703H16.5205C15.1896 10.5703 14.1104 11.6495 14.1104 12.9805V21.9248C14.1104 22.8333 13.3734 23.5703 12.4648 23.5703C11.5564 23.5702 10.8203 22.8332 10.8203 21.9248V12.9805C10.8202 11.6496 9.74106 10.5704 8.41016 10.5703H7.37207C6.61428 10.5702 6 9.95605 6 9.19824C6.00007 8.44051 6.61432 7.82625 7.37207 7.82617H17.5576ZM76.1982 23.459C76.2045 23.4614 76.2114 23.4625 76.2178 23.4648C76.1765 23.4498 76.1366 23.4334 76.0986 23.4141L76.1982 23.459ZM82.957 23.29C82.8759 23.3466 82.7895 23.3939 82.6973 23.4316L82.832 23.3682C82.9187 23.321 82.9996 23.2642 83.0752 23.1982L82.957 23.29ZM77.7598 23.1221L77.8506 23.0264C77.8535 23.0231 77.8555 23.0189 77.8584 23.0156C77.8273 23.0512 77.7952 23.0873 77.7598 23.1221ZM83.1846 23.0918L83.2734 22.9873C83.2756 22.9845 83.2772 22.9813 83.2793 22.9785C83.2495 23.0183 83.2183 23.0563 83.1846 23.0918ZM32.585 14.2158C32.0521 14.2159 31.6014 14.3568 31.2324 14.6387C30.8686 14.9205 30.5893 15.3203 30.3945 15.8379C30.1999 16.3554 30.1025 16.9651 30.1025 17.667C30.1025 18.3689 30.1999 18.9812 30.3945 19.5039C30.5893 20.0266 30.8712 20.4345 31.2402 20.7266C31.6092 21.0134 32.0572 21.1572 32.585 21.1572C33.118 21.1572 33.5695 21.0109 33.9385 20.7188C34.3073 20.4216 34.5868 20.0118 34.7764 19.4893C34.9712 18.9614 35.0684 18.3537 35.0684 17.667C35.0684 16.9855 34.9737 16.3858 34.7842 15.8682C34.5946 15.3506 34.3152 14.9454 33.9463 14.6533C33.5773 14.3612 33.1231 14.2158 32.585 14.2158ZM71.9033 14.0527C71.6436 13.3566 70.6575 13.3596 70.4023 14.0576L69.5996 16.2549C69.4089 16.7767 69.795 17.3289 70.3506 17.3291H71.9727C72.4953 17.3288 72.8677 16.8403 72.752 16.3486L72.7217 16.25L71.9033 14.0527ZM71.9307 13.6982C71.9959 13.7796 72.0504 13.8747 72.0908 13.9834L72.0205 13.8301C71.9667 13.735 71.9003 13.6538 71.8252 13.5859L71.9307 13.6982ZM70.4209 13.6504C70.4043 13.6688 70.3884 13.688 70.373 13.708C70.3885 13.6881 70.4043 13.6687 70.4209 13.6504ZM70.5967 13.4971C70.5901 13.5014 70.5846 13.5072 70.5781 13.5117C70.5926 13.5016 70.6072 13.4917 70.6221 13.4824L70.5967 13.4971ZM71.0059 13.3428L71.1514 13.333C71.3455 13.3325 71.54 13.3859 71.7061 13.4941L71.5771 13.4229C71.3984 13.3415 71.1984 13.3149 71.0059 13.3428ZM22.5537 4C22.9785 5.5447 24.1958 6.76158 25.7412 7.18457C24.1959 7.60759 22.9786 8.82357 22.5537 10.3682C22.1289 8.82344 20.9107 7.6076 19.3652 7.18457C20.9107 6.76156 22.1289 5.54476 22.5537 4ZM83.6152 10.1055L83.6104 9.91113C83.61 9.90426 83.6088 9.89745 83.6084 9.89062C83.6126 9.95993 83.6152 10.0315 83.6152 10.1055ZM73.1973 9.6123C73.1489 9.51888 73.0976 9.42755 73.042 9.33887C73.0975 9.42761 73.149 9.51886 73.1973 9.6123ZM72.4268 8.66113C72.4912 8.70551 72.5522 8.75251 72.6094 8.80273L72.4268 8.66016C72.3624 8.61582 72.2942 8.5746 72.2227 8.53613L72.4268 8.66113ZM69.7715 8.76758C69.7628 8.77487 69.7546 8.78261 69.7461 8.79004C69.798 8.74475 69.8521 8.70136 69.9092 8.66113L69.7715 8.76758ZM83.1846 8.74805L83.0898 8.6543C83.0864 8.65121 83.0826 8.64856 83.0791 8.64551C83.1154 8.67742 83.1509 8.71178 83.1846 8.74805ZM71.877 8.38965C71.9187 8.40223 71.9595 8.41557 71.999 8.43066L71.877 8.38867C71.8352 8.37612 71.7921 8.36551 71.748 8.35547L71.877 8.38965ZM73.2676 9.75391L73.2578 9.73145C73.2464 9.70734 73.2336 9.68396 73.2217 9.66016C73.2372 9.69124 73.2528 9.72231 73.2676 9.75391Z" fill="white"/>
      </g>
    </g>
    <defs>
      <filter id="filter0_d_258_7765" x="0.2" y="-1.8" width="89.4145" height="35.6" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
        <feOffset/>
        <feGaussianBlur stdDeviation="2.9"/>
        <feComposite in2="hardAlpha" operator="out"/>
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.4 0"/>
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_258_7765"/>
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_258_7765" result="shape"/>
      </filter>
      <clipPath id="clip0_258_7765">
        <rect width="90" height="32" fill="white"/>
      </clipPath>
    </defs>
  </svg>
`

/**
 * 给 Blob 添加水印
 */
async function addWatermarkToBlob(
  blob: Blob,
  watermark: WatermarkOptions
): Promise<Blob> {
  const mimeType = blob.type

  // 检查是否为图片，视频不再在前端添加水印
  if (mimeType.startsWith('image/')) {
    return addWatermarkToImage(blob, watermark)
  }

  // 视频或其他类型，直接返回原 blob
  return blob
}

/**
 * 给视频添加水印
 */
async function addWatermarkToVideo(
  blob: Blob,
  watermark: WatermarkOptions
): Promise<Blob> {
  // 直接使用 Canvas 和 MediaRecorder 方案，更稳定
  return addWatermarkToVideoWithCanvas(blob, watermark)
}

/**
 * 使用 Canvas 和 MediaRecorder 给视频添加水印
 */
async function addWatermarkToVideoWithCanvas(
  blob: Blob,
  watermark: WatermarkOptions
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      reject(new Error('Cannot create Canvas context'))
      return
    }

    // 设置视频属性
    video.muted = true
    video.playsInline = true
    video.crossOrigin = 'anonymous'
    video.preload = 'metadata'

    let mediaRecorder: MediaRecorder
    const chunks: Blob[] = []
    let isRecording = false
    let watermarkImg: HTMLImageElement | null = null // 预加载的水印图片
    let isWatermarkReady = false

    const cleanup = () => {
      try {
        if (video.src) {
          URL.revokeObjectURL(video.src)
        }
        if (
          isRecording &&
          mediaRecorder &&
          mediaRecorder.state === 'recording'
        ) {
          mediaRecorder.stop()
        }
      } catch (e) {
        console.warn('Error cleaning up resources:', e)
      }
    }

    // 错误处理
    const handleError = (error: Error) => {
      cleanup()
      reject(error)
    }

    // 预加载水印图片
    const preloadWatermark = async () => {
      try {
        if (watermark.svg) {
          watermarkImg = await createImageFromSvg(
            watermark.svg,
            watermark.size || Math.min(canvas.width, canvas.height) * 0.1
          )
        } else {
          watermarkImg = await createImageFromSvg(
            DEFAULT_WATERMARK_SVG,
            watermark.size || Math.min(canvas.width, canvas.height) * 0.1
          )
        }

        isWatermarkReady = true
      } catch (error) {
        isWatermarkReady = false
      }
    }

    // 同步绘制水印（避免异步问题）
    const drawWatermarkSync = (ctx: CanvasRenderingContext2D) => {
      try {
        const { position = 'bottom-right', opacity = 0.8 } = watermark

        ctx.save()
        ctx.globalAlpha = opacity

        if (isWatermarkReady && watermarkImg) {
          // 使用预加载的图片水印
          const { x, y } = calculateWatermarkPosition(
            position,
            canvas.width,
            canvas.height,
            watermarkImg.width,
            watermarkImg.height
          )

          ctx.drawImage(watermarkImg, x, y)
        } else {
          // 使用文字水印作为降级方案
          drawTextWatermarkSync(
            ctx,
            canvas.width,
            canvas.height,
            position,
            opacity
          )
        }

        ctx.restore()
      } catch (error) {
        // 最基础的降级方案
        ctx.save()
        ctx.fillStyle = `rgba(255, 255, 255, ${watermark.opacity || 0.8})`
        ctx.font = '16px Arial'
        ctx.fillText('Tipsy AI', canvas.width - 80, canvas.height - 20)
        ctx.restore()
      }
    }

    // 同步绘制文字水印
    const drawTextWatermarkSync = (
      ctx: CanvasRenderingContext2D,
      canvasWidth: number,
      canvasHeight: number,
      position: string,
      opacity: number
    ) => {
      const fontSize = Math.min(canvasWidth, canvasHeight) * 0.05
      ctx.font = `bold ${fontSize}px Arial`
      ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`
      ctx.strokeStyle = `rgba(0, 0, 0, ${opacity * 0.5})`
      ctx.lineWidth = 2
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      const text = 'Tipsy AI'
      const textMetrics = ctx.measureText(text)
      const textWidth = textMetrics.width
      const textHeight = fontSize

      const padding = 20
      let x: number, y: number

      switch (position) {
        case 'top-left':
          x = padding + textWidth / 2
          y = padding + textHeight / 2
          break
        case 'top-right':
          x = canvasWidth - padding - textWidth / 2
          y = padding + textHeight / 2
          break
        case 'bottom-left':
          x = padding + textWidth / 2
          y = canvasHeight - padding - textHeight / 2
          break
        case 'bottom-right':
        default:
          x = canvasWidth - padding - textWidth / 2
          y = canvasHeight - padding - textHeight / 2
          break
      }

      ctx.strokeText(text, x, y)
      ctx.fillText(text, x, y)
    }

    // 检查浏览器支持
    const getSupportedMimeType = (): string => {
      const types = [
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm;codecs=h264',
        'video/webm',
        'video/mp4',
      ]

      for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) {
          return type
        }
      }

      throw new Error('Browser does not support video recording')
    }

    video.onloadedmetadata = async () => {
      try {
        // 设置 canvas 尺寸
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight

        if (canvas.width === 0 || canvas.height === 0) {
          handleError(new Error('Invalid video dimensions'))
          return
        }

        // 预加载水印
        await preloadWatermark()

        // 获取支持的 MIME 类型
        const mimeType = getSupportedMimeType()

        // 创建视频流
        const stream = canvas.captureStream(30) // 30 FPS

        // 设置 MediaRecorder
        mediaRecorder = new MediaRecorder(stream, {
          mimeType,
          videoBitsPerSecond: 2500000, // 2.5 Mbps
        })

        mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            chunks.push(event.data)
          }
        }

        mediaRecorder.onstop = () => {
          try {
            if (chunks.length === 0) {
              handleError(new Error('No video data recorded'))
              return
            }

            const finalBlob = new Blob(chunks, { type: mimeType })
            cleanup()
            resolve(finalBlob)
          } catch (error) {
            handleError(
              new Error(
                `Failed to create final video: ${error instanceof Error ? error.message : String(error)}`
              )
            )
          }
        }

        mediaRecorder.onerror = () => {
          handleError(new Error('MediaRecorder recording error'))
        }

        // 开始录制
        mediaRecorder.start(100) // 每100ms一个数据块
        isRecording = true

        // 开始播放和渲染
        video.currentTime = 0
        video
          .play()
          .then(() => {
            renderFrames()
          })
          .catch((error) => {
            handleError(
              new Error(
                `Video playback failed: ${error instanceof Error ? error.message : String(error)}`
              )
            )
          })
      } catch (error) {
        handleError(
          new Error(
            `Video processing initialization failed: ${error instanceof Error ? error.message : String(error)}`
          )
        )
      }
    }

    // 渲染帧函数（改为同步）
    const renderFrames = () => {
      try {
        if (video.ended || video.paused) {
          // 视频结束，停止录制
          setTimeout(() => {
            if (mediaRecorder && mediaRecorder.state === 'recording') {
              mediaRecorder.stop()
              isRecording = false
            }
          }, 200)
          return
        }

        // 清除画布
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        // 绘制当前视频帧
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

        // 同步添加水印（避免闪烁）
        drawWatermarkSync(ctx)

        // 继续下一帧
        requestAnimationFrame(renderFrames)
      } catch (error) {
        handleError(
          new Error(
            `Frame rendering failed: ${error instanceof Error ? error.message : String(error)}`
          )
        )
      }
    }

    // 视频结束事件
    video.onended = () => {
      setTimeout(() => {
        if (mediaRecorder && mediaRecorder.state === 'recording') {
          mediaRecorder.stop()
          isRecording = false
        }
      }, 300)
    }

    // 视频加载错误
    video.onerror = () => {
      handleError(new Error('Video loading failed'))
    }

    // 设置视频源并开始加载
    video.src = URL.createObjectURL(blob)
    video.load()
  })
}

/**
 * 在 Canvas 上绘制水印
 */
async function drawWatermark(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  watermark: WatermarkOptions
): Promise<void> {
  const {
    svg = DEFAULT_WATERMARK_SVG,
    position = 'bottom-right',
    size = Math.min(canvasWidth, canvasHeight) * 0.1,
    opacity = 0.8,
  } = watermark

  try {
    // 创建水印图片
    const watermarkImg = await createImageFromSvg(svg, size)

    // 计算水印位置
    const { x, y } = calculateWatermarkPosition(
      position,
      canvasWidth,
      canvasHeight,
      watermarkImg.width,
      watermarkImg.height
    )

    // 确保位置在画布范围内
    if (
      x < 0 ||
      y < 0 ||
      x + watermarkImg.width > canvasWidth ||
      y + watermarkImg.height > canvasHeight
    ) {
      console.warn('Out of range.', {
        position: { x, y },
        watermarkSize: {
          width: watermarkImg.width,
          height: watermarkImg.height,
        },
        canvasSize: { width: canvasWidth, height: canvasHeight },
      })
    }

    // 保存当前状态
    ctx.save()

    // 设置透明度
    ctx.globalAlpha = opacity

    // 绘制水印
    ctx.drawImage(watermarkImg, x, y)

    // 恢复状态
    ctx.restore()
  } catch (error) {
    // 降级方案：绘制简单文字水印
    drawTextWatermark(ctx, canvasWidth, canvasHeight, position, opacity)
  }
}

/**
 * 绘制简单文字水印（降级方案）
 */
function drawTextWatermark(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  position: string,
  opacity: number
): void {
  try {
    ctx.save()

    // 设置文字样式
    const fontSize = Math.min(canvasWidth, canvasHeight) * 0.05
    ctx.font = `${fontSize}px Arial`
    ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`
    ctx.strokeStyle = `rgba(0, 0, 0, ${opacity * 0.5})`
    ctx.lineWidth = 2
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    const text = 'Tipsy AI'
    const textMetrics = ctx.measureText(text)
    const textWidth = textMetrics.width
    const textHeight = fontSize

    // 计算文字位置
    const padding = 20
    let x: number, y: number

    switch (position) {
      case 'top-left':
        x = padding + textWidth / 2
        y = padding + textHeight / 2
        break
      case 'top-right':
        x = canvasWidth - padding - textWidth / 2
        y = padding + textHeight / 2
        break
      case 'bottom-left':
        x = padding + textWidth / 2
        y = canvasHeight - padding - textHeight / 2
        break
      case 'bottom-right':
      default:
        x = canvasWidth - padding - textWidth / 2
        y = canvasHeight - padding - textHeight / 2
        break
    }

    // 绘制文字（带描边）
    ctx.strokeText(text, x, y)
    ctx.fillText(text, x, y)

    ctx.restore()
  } catch (error) {
    console.error(error)
  }
}

/**
 * 从 SVG 创建图片
 */
function createImageFromSvg(
  svgString: string,
  size: number
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    // 超时处理
    const timeout = setTimeout(() => {
      reject(new Error('SVG image loading timeout'))
    }, 5000)

    img.onload = () => {
      clearTimeout(timeout)
      resolve(img)
    }

    img.onerror = () => {
      clearTimeout(timeout)
      reject(new Error('Failed to create watermark image'))
    }

    try {
      // 处理SVG字符串
      let processedSvg = svgString.trim()

      // 如果不是完整的SVG，添加基本结构
      if (!processedSvg.startsWith('<svg')) {
        processedSvg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size * 0.3}" viewBox="0 0 ${size} ${size * 0.3}">
            ${processedSvg}
          </svg>
        `
      }

      // 调整 SVG 尺寸
      const parser = new DOMParser()
      const svgDoc = parser.parseFromString(processedSvg, 'image/svg+xml')

      // 检查解析错误
      const parserError = svgDoc.querySelector('parsererror')
      if (parserError) {
        throw new Error('SVG parsing failed: ' + parserError.textContent)
      }

      const svgElement = svgDoc.documentElement
      if (!svgElement || svgElement.tagName !== 'svg') {
        throw new Error('Invalid SVG structure')
      }

      // 设置尺寸
      svgElement.setAttribute('width', size.toString())
      svgElement.setAttribute('height', (size * 0.3).toString())

      // 确保有viewBox
      if (!svgElement.getAttribute('viewBox')) {
        svgElement.setAttribute('viewBox', `0 0 ${size} ${size * 0.3}`)
      }

      const serializer = new XMLSerializer()
      const svgStr = serializer.serializeToString(svgElement)

      // 创建Blob URL
      const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(blob)

      // 清理URL
      img.onload = () => {
        clearTimeout(timeout)
        URL.revokeObjectURL(url)
        resolve(img)
      }

      img.src = url
    } catch (error) {
      clearTimeout(timeout)
      reject(error)
    }
  })
}

/**
 * 计算水印位置
 */
function calculateWatermarkPosition(
  position: string,
  canvasWidth: number,
  canvasHeight: number,
  watermarkWidth: number,
  watermarkHeight: number
): { x: number; y: number } {
  const padding = 20

  switch (position) {
    case 'top-left':
      return { x: padding, y: padding }
    case 'top-right':
      return { x: canvasWidth - watermarkWidth - padding, y: padding }
    case 'bottom-left':
      return { x: padding, y: canvasHeight - watermarkHeight - padding }
    case 'bottom-right':
    default:
      return {
        x: canvasWidth - watermarkWidth - padding,
        y: canvasHeight - watermarkHeight - padding,
      }
  }
}

/**
 * 给图片添加水印
 */
async function addWatermarkToImage(
  blob: Blob,
  watermark: WatermarkOptions
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      reject(new Error('Cannot create Canvas context'))
      return
    }

    img.onload = async () => {
      try {
        canvas.width = img.width
        canvas.height = img.height

        // 绘制原图
        ctx.drawImage(img, 0, 0)

        // 添加水印
        await drawWatermark(ctx, canvas.width, canvas.height, watermark)

        // 转换为 Blob
        canvas.toBlob(
          (newBlob) => {
            if (newBlob) {
              resolve(newBlob)
            } else {
              reject(new Error('Failed to convert image'))
            }
          },
          blob.type,
          0.9
        )
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = URL.createObjectURL(blob)
  })
}

export function downloadTxt(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * 保存 TXT 文本并返回保存状态（支持 File System Access API，含回退方案）
 */
export async function saveTxtWithStatus(
  filename: string,
  content: string
): Promise<'saved' | 'canceled' | 'triggered' | 'error'> {
  try {
    if (typeof (window as any).showSaveFilePicker === 'function') {
      const handle = await (window as any).showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: 'Text Files',
            accept: { 'text/plain': ['.txt'] },
          },
        ],
      })

      const writable = await handle.createWritable()
      await writable.write(
        new Blob([content], { type: 'text/plain;charset=utf-8' })
      )
      await writable.close()
      return 'saved'
    }
  } catch (err: any) {
    if (err && err.name === 'AbortError') {
      return 'canceled'
    }
    console.error('saveTxtWithStatus error:', err)
    return 'error'
  }

  // 不支持 File System Access API 的浏览器，触发传统下载（无法检测成功与否）
  try {
    downloadTxt(filename, content)
    return 'triggered'
  } catch (e) {
    console.error('downloadTxt fallback error:', e)
    return 'error'
  }
}
